import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  walletTransactionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type {
  InitiateTransactionRequestDto,
  TransactionDto,
} from "@/features/wallet/api/dto";

/**
 * Initie une transaction (`POST /Transactions/initiate`) — étape 1 du flux
 * §5.3 : contrôle des plafonds KYC, calcul des frais par wallet-service,
 * pose du blocage, création en AWAITING_LEDGER (422 si plafond dépassé,
 * portefeuille inactif ou solde insuffisant).
 *
 * Idempotency-Key OBLIGATOIRE (guide §4.7) : la clé est persistée par
 * empreinte du corps (même tentative après coupure réseau / reload) ;
 * un 409 attend puis rejoue la même clé ; un 422 oublie la clé.
 */
export const useInitiateTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "transactions", "initiate"],
    mutationFn: (body: InitiateTransactionRequestDto) =>
      mutateIdempotent<TransactionDto>({
        scopeId: "wallet.transactions.initiate",
        url: walletEndpoints.transactions.initiate,
        body,
      }),
    onSuccess: () => {
      return invalidateAfterMutation(
        queryClient,
        walletTransactionCacheKeys(),
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useInitiateTransaction failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
